import type { Grade, Skill, Subject, SubjectId } from '../domain.js';
import { MATH_SKILLS } from './math.js';
import { UKRAINIAN_SKILLS } from './ukrainian.js';
import { ENGLISH_SKILLS } from './english.js';
import { SCIENCE_SKILLS } from './science.js';
import { LOGIC_SKILLS } from './logic.js';

export const SUBJECTS: Record<SubjectId, Subject> = {
  math: {
    id: 'math',
    title: 'Математика',
    description: 'Рахунок, дроби, задачі, геометрія, величини.',
    theme: 'blaze',
    icon: 'subject-math',
  },
  ukrainian: {
    id: 'ukrainian',
    title: 'Українська мова',
    description: 'Орфографія, частини мови, речення, читання й розуміння.',
    theme: 'quill',
    icon: 'subject-ukrainian',
  },
  english: {
    id: 'english',
    title: 'Англійська',
    description: 'Лексика, граматика, читання та аудіювання.',
    theme: 'nova',
    icon: 'subject-english',
  },
  science: {
    id: 'science',
    title: 'Природознавство',
    description: 'Жива природа, Земля і космос, фізика, хімія, географія.',
    theme: 'flora',
    icon: 'subject-science',
  },
  logic: {
    id: 'logic',
    title: 'Логіка',
    description: 'Закономірності, міркування, простір, гроші, алгоритми.',
    theme: 'circuit',
    icon: 'subject-logic',
  },
};

/** Повний реєстр навичок. Єдине джерело правди про зміст навчання. */
export const ALL_SKILLS: Skill[] = [
  ...MATH_SKILLS,
  ...UKRAINIAN_SKILLS,
  ...ENGLISH_SKILLS,
  ...SCIENCE_SKILLS,
  ...LOGIC_SKILLS,
];

const SKILL_BY_ID = new Map<string, Skill>(ALL_SKILLS.map((s) => [s.id, s]));

export function getSkill(id: string): Skill | undefined {
  return SKILL_BY_ID.get(id);
}

export function requireSkill(id: string): Skill {
  const skill = SKILL_BY_ID.get(id);
  if (!skill) throw new Error(`Невідома навичка: ${id}`);
  return skill;
}

export function skillsBySubject(subject: SubjectId): Skill[] {
  return ALL_SKILLS.filter((s) => s.subject === subject);
}

/**
 * Навички, доречні для класу. Беремо на клас ширше вниз і вгору:
 * вниз — щоб закривати прогалини, вгору — щоб було куди рости.
 */
export function skillsForGrade(grade: Grade, subject?: SubjectId): Skill[] {
  return ALL_SKILLS.filter((s) => {
    if (subject && s.subject !== subject) return false;
    const [from, to] = s.gradeBand;
    return grade >= from - 1 && grade <= to + 1;
  });
}

/** Чи всі передумови навички опановані (mastery ≥ поріг). */
export function prerequisitesMet(
  skill: Skill,
  masteryByskillId: ReadonlyMap<string, number>,
  threshold = 0.6,
): boolean {
  return skill.prerequisites.every((p) => (masteryByskillId.get(p) ?? 0) >= threshold);
}

/** Валідація дерева: усі передумови існують і немає циклів. */
export function validateCurriculum(): string[] {
  const errors: string[] = [];
  for (const skill of ALL_SKILLS) {
    for (const p of skill.prerequisites) {
      if (!SKILL_BY_ID.has(p)) {
        errors.push(`${skill.id}: невідома передумова "${p}"`);
      }
    }
    if (skill.gradeBand[0] > skill.gradeBand[1]) {
      errors.push(`${skill.id}: некоректний gradeBand`);
    }
  }

  const state = new Map<string, 0 | 1 | 2>();
  const walk = (id: string, path: string[]): void => {
    if (state.get(id) === 2) return;
    if (state.get(id) === 1) {
      errors.push(`Цикл у передумовах: ${[...path, id].join(' → ')}`);
      return;
    }
    state.set(id, 1);
    for (const p of SKILL_BY_ID.get(id)?.prerequisites ?? []) {
      walk(p, [...path, id]);
    }
    state.set(id, 2);
  };
  for (const skill of ALL_SKILLS) walk(skill.id, []);

  return errors;
}

export { MATH_SKILLS, UKRAINIAN_SKILLS, ENGLISH_SKILLS, SCIENCE_SKILLS, LOGIC_SKILLS };
